import { UserAuthenticationContext } from '../../../../interfaces/src/lib/enums/index';
import CryptoJS from 'crypto-js';

// Detects what device is used (mobile or desktop) with the help of user agent
const isMobileDevice = () => {
  const userAgent = navigator.userAgent;
  const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent.toLowerCase()
  );
  const isSmallScreen = window.innerWidth <= 768;

  return isMobileUserAgent || isSmallScreen;
};

// Detects if the user is authenticated or not.
// Retrieves the user object from the session and checks if it contains a not empty account name
export function isUserAuthenticated() {
  const user = getUserFromSession();
  return !!(user?.AccountName && user.AccountName.length > 0);
}

export function getUserAuthenticationContext() {
  return isUserAuthenticated()
    ? UserAuthenticationContext.Authorized
    : UserAuthenticationContext.Unknown;
}

// Decrypts and parses an input string
export function iUnwrap(input: any) {
  // Check if the input string looks like Base64 (or Base64 URL-safe) format
  const isBase64 = /^(?:[A-Za-z0-9+/]*|[A-Za-z0-9\-_]*)={0,2}$/i.test(input);
  // Check if the input is a plain object (not null and not an array)
  const isObj = typeof input === 'object' && input !== null && !(input instanceof Array);
  if (typeof input === 'string' && !isBase64) return input;
  if (isObj) return input;

  // Define AES decryption key and IV (initialization vector)
  const key = CryptoJS.enc.Utf8.parse('c8d58135b8ca3082');
  const iv = CryptoJS.enc.Utf8.parse('9da3cd41b0599172');

  // Replace URL-safe Base64 characters with standard Base64 ones,
  // then decrypt the input using AES with CBC mode and PKCS7 padding
  const decrypted = CryptoJS.AES.decrypt(input.replace(/-/gi, '/').replace(/_/gi, '+'), key, {
    iv,
    keySize: 128 / 8,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  // Convert decrypted bytes to UTF-8 string
  const dataJson = decrypted.toString(CryptoJS.enc.Utf8);

  // Try to parse the JSON string if it's not empty, otherwise return the input
  if (dataJson.length) return JSON.parse(dataJson);
  return input;
}

// Retrieves and decodes the user data stored in sessionStorage under the key 'user'.
// It attempts to decrypt and parse the stored token using the `iUnwrap` function.
export function getUserFromSession() {
  const userToken = sessionStorage['user'];
  if (userToken) {
    const userTokenDecrypt = iUnwrap(userToken);
    try {
      return JSON.parse(userTokenDecrypt);
    } catch (error) {
      return null;
    }
  }
  return null;
}

// Generates a list of context variable strings based on the component name,
// user device type, authentication status, current page, and optional query parameters.
export const getContextVariables = (component: string, isDealPage: boolean): string[] => {
  const params = new URLSearchParams(window.location.search);
  const userDevice = isMobileDevice() ? 'mobile' : 'desktop';

  // Get the current results page from the query (default to page_1)
  const currentSearchResultsPage = `page_${params.get('page') || '1'}`;
  const customContext = params.get('context') || '';
  const activeComponent = isDealPage ? `${component}_${getPagePathName()}` : component;
  const userAuthStatus = getUserAuthenticationContext();
  // Define the base set of context variable strings
  const baseContextVariables = [
    activeComponent,
    userDevice,
    userAuthStatus,
    currentSearchResultsPage,
    `${activeComponent}_${userDevice}`,
    `${activeComponent}_${userAuthStatus}`,
    `${activeComponent}_${currentSearchResultsPage}`,
    `${activeComponent}_${userDevice}_${userAuthStatus}_${currentSearchResultsPage}`,
  ];

  // If a custom context string exists, add it to the context variable list
  return customContext ? [...baseContextVariables, customContext] : baseContextVariables;
};

// Helper function to get a cleaned version of the current page path.
// Converts something like '/deals/summer-sale.html' to 'deals-summer-sale'.
const getPagePathName = () => {
  const windowPathName = window.location.pathname;
  return windowPathName.slice(1).replace(/\//g, '-').replace('.html', '');
};
